import PageLoadedQueue from "./PageLoadedQueue";

declare global {
    interface Window {
        SizeChanged: SizeChanged;
    }
}

/**
 * Triggers event on the window if the width or the height was changed
 *
 * Usage:
 * window.addEventListener(SizeChanged.widthChangedEventName, () => {
 *     console.log('width changed');
 * });
 *
 * window.addEventListener(SizeChanged.heightChangedEventName, () => {
 *     console.log('height changed');
 * });
 */
class SizeChanged {
    // Event names
    public static widthChangedEventName: string = 'widthChange';
    public static heightChangedEventName: string = 'heightChange';

    /**
     * The current width of the viewport
     * @protected
     */
    protected static currentWidth: number = undefined;

    /**
     * The current height of the viewport
     * @protected
     */
    protected static currentHeight: number = undefined;

    /**
     * Initialize size changed
     */
    static initialize() {
        SizeChanged.updateWidths();
        SizeChanged.initEventListener();
    }

    /**
     * Update the current width and height of the viewport
     *
     * @protected
     */
    protected static updateWidths = (): void => {
        this.currentWidth = document.documentElement.clientWidth;
        this.currentHeight = document.documentElement.clientHeight;
    }

    /**
     * Returns the width of the viewport (document-element)
     * Equals to `$(window).width()`
     *
     * @return {number}
     */
    public static get clientWidth(): number {
        return document.documentElement.clientWidth;
    }

    /**
     * Returns the height of the viewport (document-element)
     * Equals to `$(window).height()`
     *
     * @return {number}
     */
    public static get clientHeight(): number {
        return document.documentElement.clientHeight;
    }

    /**
     * Initializes all Event-Listeners
     *
     * @protected
     */
    protected static initEventListener = () => {
        window.addEventListener('resize', () => {
            SizeChanged.fireWidthChangedEvent();
            SizeChanged.fireHeightChangedEvent();
            SizeChanged.updateWidths();
        }, {passive: true});
    }

    /**
     * Fires the `widthChange` Event if the width of the window was changed
     *
     * @protected
     */
    protected static fireWidthChangedEvent = () => {
        if (SizeChanged.currentWidth !== SizeChanged.clientWidth) {
            window.dispatchEvent(new Event(SizeChanged.widthChangedEventName));
        }
    }

    /**
     * Fires the `heightChange` Event if the height of the window was changed
     *
     * @protected
     */
    protected static fireHeightChangedEvent = () => {
        if (SizeChanged.currentHeight !== SizeChanged.clientHeight) {
            window.dispatchEvent(new Event(SizeChanged.heightChangedEventName));
        }
    }
}

export default SizeChanged;

PageLoadedQueue.addFunction(SizeChanged.initialize);
